import React, { useState } from 'react';
import { HEALTH_STATES } from '@/const';
import { cn } from '@/lib/utils';
import { NotificationAlert, NotificationAttachment } from '@/data/notifications';
import { useFetchResource } from '@/hooks';
import { Skeleton } from '@/components/ui/skeleton';
import { colors, HealthSeverity } from '@/constants/colors';

interface AttachmentItemProps {
  resourceId: string;
  className?: string;
}

const AttachmentItem = ({ resourceId, className }: AttachmentItemProps) => {
  const { resource, isLoadingResource, isError, retry } = useFetchResource(resourceId);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset loaded state when resource URL changes (e.g. new attachment selected)
  React.useEffect(() => {
    setImageLoaded(false);
  }, [resource]);

  const showSkeleton = isLoadingResource || (resource && !imageLoaded);

  if (isError) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 text-[#55687D] min-h-[200px]', className)}>
        <span>Failed to load attachment</span>
        <button onClick={retry} className="text-primary-600 hover:underline text-sm">
          Try Again
        </button>
      </div>
    );
  }

  if (!isLoadingResource && !resource) {
    return (
      <div className={cn('flex items-center justify-center min-h-[200px] text-[#55687D]', className)}>No data</div>
    );
  }

  return (
    <div className="relative">
      {showSkeleton && <Skeleton className={cn('min-h-[200px] w-full', className)} />}
      {resource && (
        <img
          src={resource}
          className={cn('w-full', showSkeleton && 'absolute invisible', className)}
          alt="Notification attachment"
          onLoad={() => setImageLoaded(true)}
        />
      )}
    </div>
  );
};

interface NotificationModalTitleProps {
  notification: NotificationAlert;
}

export const NotificationModalTitle = ({ notification }: NotificationModalTitleProps) => {
  const Icon = HEALTH_STATES[notification.severity].icon;

  return (
    <div className="flex items-center gap-2">
      <div>
        <div className="w-10 h-10 bg-[#EDF2F7] flex justify-center items-center rounded-full">
          <Icon className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`} />
        </div>
      </div>

      <div className="font-semibold">
        {notification.asset_ui_name}
        {notification.component_name ? ` / ${notification.component_name}` : null} – {notification.event_ui_name}
      </div>
    </div>
  );
};

interface NotificationModalContentProps {
  notification: NotificationAlert;
}

export const NotificationModalContent = ({ notification }: NotificationModalContentProps) => {
  const [activeResource, setActiveResource] = useState(0);
  const attachments = notification.attachments;

  return (
    <>
      {!!notification.content && <div className="py-4">{notification.content}</div>}
      {!!attachments.length && <AttachmentItem resourceId={attachments[activeResource]?.content} />}
      {attachments.length > 1 && (
        <div className="flex gap-2">
          {attachments.map((attachment, index) => (
            <div
              className={cn(
                'max-w-[100px] my-2 p-1 cursor-pointer',
                index === activeResource ? 'border-2 border-black' : ''
              )}
              key={attachment.content}
              onClick={() => setActiveResource(index)}
            >
              <AttachmentItem resourceId={attachment.content} className="min-h-0" />
            </div>
          ))}
        </div>
      )}
    </>
  );
};
